<template>
  <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-2 sm:p-4">
    <!-- Role Header -->
    <div class="flex items-start justify-between mb-3">
      <div>
        <h4 class="font-medium text-gray-900 dark:text-white">
          {{ preference.role || 'Medewerker' }}
          <span class="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
            {{ preference.dpw }} dagen/week
          </span>
        </h4>
        <div
          v-if="teamNames.length || experienceLevels.length"
          class="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mt-1"
        >
          <span v-if="teamNames.length">Teams: {{ teamNames.join(', ') }}</span>
          <span v-if="experienceLevels.length">Niveau: {{ experienceLevels.join(', ') }}</span>
        </div>
      </div>

      <!-- Assignment status indicator -->
      <div v-if="assignment" class="flex items-center gap-2">
        <CheckCircleIcon class="h-5 w-5 text-green-500" />
        <span class="text-sm text-green-700 dark:text-green-400 font-medium">Toegewezen</span>
      </div>
    </div>

    <!-- Already assigned employee -->
    <div v-if="assignment" class="mb-4 p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div
            class="h-10 w-10 rounded-full bg-green-200 dark:bg-green-800 flex items-center justify-center"
          >
            <UserIcon class="h-6 w-6 text-green-700 dark:text-green-300" />
          </div>
          <div>
            <div class="font-medium text-gray-900 dark:text-white">
              {{ assignment.employee_name }}
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400">
              {{ assignment.employee_function }}
            </div>
          </div>
        </div>
        <button
          @click="$emit('remove-assignment', assignment)"
          class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm"
        >
          Verwijderen
        </button>
      </div>
    </div>

    <!-- Preferred employees selection -->
    <div v-else>
      <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">Voorgestelde medewerkers:</p>

      <div
        v-if="preferredEmployees.length === 0"
        class="text-sm text-gray-400 dark:text-gray-500 mb-3"
      >
        Geen voorkeur medewerkers opgegeven
      </div>

      <div v-else class="space-y-2 mb-3">
        <SourcingEmployeeOption
          v-for="empId in preferredEmployees"
          :key="empId"
          :employee-id="empId"
          :employee-name="getEmployeeName(empId)"
          :employee-function="getEmployeeFunction(empId)"
          :employee-teams="getEmployeeTeams(empId)"
          :input-name="`employee-${prospect.id}-${preference.id || prefIndex}`"
          :is-selected="selectedEmployeeId === empId"
          :is-pl-recommended="isPlRecommended(empId)"
          :match-stats="getMatchStats(empId)"
          :other-proposals="selectedEmployeeId === empId ? getOtherProposals(empId) : []"
          :contract-end-date="getEmployeeContractEndDate(empId)"
          :contract-start-date="getEmployeeContractStartDate(empId)"
          :project-end-date="periodEnd"
          @select="handleEmployeeSelect"
        />
      </div>

      <!-- Other employee option -->
      <div class="flex items-center gap-2">
        <button
          @click="$emit('open-employee-selector')"
          class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm flex items-center gap-1"
        >
          <PlusIcon class="h-4 w-4" />
          Andere medewerker toevoegen
        </button>

        <!-- Selected custom employee display -->
        <div v-if="customSelectedEmployee" class="flex items-center gap-2 ml-4">
          <span class="text-sm font-medium text-gray-900 dark:text-white">
            {{ customSelectedEmployee.name }}
          </span>
          <button
            @click="$emit('clear-custom-employee')"
            class="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
          >
            <XMarkIcon class="h-4 w-4" />
          </button>
        </div>
      </div>

      <!-- Assign button -->
      <div class="mt-4 flex justify-end">
        <button
          @click="$emit('assign')"
          :disabled="!canAssign || isAssigning"
          class="px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors"
          :class="canAssign ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-300 cursor-not-allowed'"
        >
          <span v-if="isAssigning">Toewijzen...</span>
          <span v-else>Definitief toewijzen</span>
        </button>
      </div>

      <!-- Availability Section -->
      <SourcingAvailabilitySection
        :prospect-id="prospect.id"
        :pref-id="preference.id || prefIndex"
        :preferred-employees="preferredEmployees"
        :employee-availability-data="employeeAvailabilityData"
        :selected-employee-id="selectedEmployeeId"
        :expanded-graphs="expandedGraphs"
        :employees-map="employeesMap"
        :is-expanded="isAvailabilityExpanded"
        :period-start="periodStart"
        :period-end="periodEnd"
        :potential-dpw="Number(preference.dpw) || 0"
        :potential-label="preference.role ? `${preference.role} (nieuw)` : 'Dit project'"
        :exclude-proposed-preference-id="preference.id ?? null"
        @toggle-section="$emit('toggle-availability')"
        @toggle-graph="empId => $emit('toggle-employee-graph', empId)"
      />
    </div>

    <!-- Notes -->
    <div v-if="preference.notes" class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
      <p class="text-xs text-gray-500 dark:text-gray-400">Notitie: {{ preference.notes }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { CheckCircleIcon, UserIcon, PlusIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import SourcingEmployeeOption from './SourcingEmployeeOption.vue'
import SourcingAvailabilitySection from './SourcingAvailabilitySection.vue'

const props = defineProps({
  prospect: {
    type: Object,
    required: true
  },
  preference: {
    type: Object,
    required: true
  },
  prefIndex: {
    type: Number,
    required: true
  },
  assignment: {
    type: Object,
    default: null
  },
  selectedEmployeeId: {
    type: String,
    default: null
  },
  customSelectedEmployee: {
    type: Object,
    default: null
  },
  matchStats: {
    type: Object,
    default: () => ({})
  },
  employeeAvailabilityData: {
    type: Object,
    default: () => ({})
  },
  employeesMap: {
    type: Object,
    default: () => ({})
  },
  teamsMap: {
    type: Object,
    default: () => ({})
  },
  isAssigning: {
    type: Boolean,
    default: false
  },
  canAssign: {
    type: Boolean,
    default: false
  },
  expandedGraphs: {
    type: Object,
    default: () => ({})
  },
  isAvailabilityExpanded: {
    type: Boolean,
    default: true
  },
  getOtherProposalsFn: {
    type: Function,
    default: () => () => []
  }
})

const emit = defineEmits([
  'select-employee',
  'assign',
  'remove-assignment',
  'open-employee-selector',
  'clear-custom-employee',
  'toggle-availability',
  'toggle-employee-graph'
])

// Computed
const preferredEmployees = computed(() => {
  if (!props.preference.preferred_employees) return []
  return Array.isArray(props.preference.preferred_employees)
    ? props.preference.preferred_employees
    : JSON.parse(props.preference.preferred_employees || '[]')
})

const teamNames = computed(() => {
  const teamIds = props.preference.teams
  if (!teamIds) return []
  const ids = Array.isArray(teamIds) ? teamIds : JSON.parse(teamIds || '[]')
  return ids.map(id => props.teamsMap[id]).filter(Boolean)
})

const experienceLevels = computed(() => {
  const levels = props.preference.experience_levels
  if (!levels) return []
  return Array.isArray(levels) ? levels : JSON.parse(levels || '[]')
})

const periodStart = computed(() => {
  const date = props.preference.start_date || props.prospect.start_date
  return date ? date.split('T')[0] : null
})

const periodEnd = computed(() => {
  const date = props.preference.end_date || props.prospect.expected_closing_date
  return date ? date.split('T')[0] : null
})

// Methods
function getEmployeeName(empId) {
  return props.employeesMap[empId]?.name || empId
}

function getEmployeeFunction(empId) {
  return props.employeesMap[empId]?.function || ''
}

function getEmployeeTeams(empId) {
  const emp = props.employeesMap[empId]
  if (!emp?.teams) return ''
  return emp.teams.map(t => t.name).join(', ')
}

function getEmployeeContractEndDate(empId) {
  return props.employeesMap[empId]?.contract_end_date || null
}

function getEmployeeContractStartDate(empId) {
  return props.employeesMap[empId]?.contract_start_date || null
}

function isPlRecommended(empId) {
  return props.preference.pl_selected_employee_id === empId
}

function getMatchStats(empId) {
  const dpw = parseFloat(props.preference.dpw) || 0
  const key = `${empId}-${dpw}`
  return props.matchStats[key] || null
}

function getOtherProposals(empId) {
  return props.getOtherProposalsFn(empId, props.prospect.id)
}

function handleEmployeeSelect(empId) {
  emit('select-employee', empId)
}
</script>
